import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { receptionApi } from './reception.api';
import { receptionKeys } from './reception.queries';
import type {
  VerifyCountRequest,
  ReportDiscrepancyRequest,
  VerifyItemRequest,
  ShipmentsListParams
} from '../types';

export function useVerifyQrByNumberMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('reception');

  return useMutation({
    mutationFn: (shipmentNumber: string) =>
      receptionApi.verifyQrByNumber(shipmentNumber),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('qrVerified')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receptionKeys.details() });
    }
  });
}

export function useVerifyQrMutation(options?: { silent?: boolean }) {
  const queryClient = useQueryClient();
  const t = useTranslations('reception');

  return useMutation({
    mutationFn: (shipmentId: number) => receptionApi.verifyQr(shipmentId),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('qrVerified'),
      ...(options?.silent && { skipGlobalSuccessToast: true })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receptionKeys.details() });
    }
  });
}

export function useVerifyCountMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('reception');

  return useMutation({
    mutationFn: ({
      sessionId,
      data
    }: {
      sessionId: number;
      data: VerifyCountRequest;
    }) => receptionApi.verifyCount(sessionId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('countVerified')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receptionKeys.details() });
    }
  });
}

export function useReportDiscrepancyMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('reception');

  return useMutation({
    mutationFn: ({
      sessionId,
      data
    }: {
      sessionId: number;
      data: ReportDiscrepancyRequest;
    }) => receptionApi.reportDiscrepancy(sessionId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('discrepancyReported')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receptionKeys.details() });
    }
  });
}

export function useVerifyItemMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('reception');

  return useMutation({
    mutationFn: ({
      sessionId,
      data
    }: {
      sessionId: number;
      data: VerifyItemRequest;
    }) => receptionApi.verifyItem(sessionId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('itemVerified')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receptionKeys.details() });
    }
  });
}

export function useCompleteSessionMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('reception');

  return useMutation({
    mutationFn: (sessionId: number) => receptionApi.complete(sessionId),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('sessionCompleted')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receptionKeys.all });
    }
  });
}

export function useBulkDeleteShipmentsMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('reception');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<ShipmentsListParams> }
    ) => receptionApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receptionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: receptionKeys.metrics() });
    }
  });
}
